import { SkeletonLoader } from '@toggle/design-system';
import { Entity, FinancialStatementType, TimeFrame } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import * as Shared from '~/views/asset-overview/AssetOverview.styles';

import { FinancialTable } from './financial-table/FinancialTable';
import { useFinancialStatement } from './use-financial-statement/useFinancialStatement';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys } from '~/routes/query-params';

export interface AssetFinancialsProps {
  entity: Entity;
}

const timeFrames: TimeFrame[] = ['annual', 'quarterly'];
// order matters here
const financialStatementButtonGroupLabels: FinancialStatementType[] = [
  'incomeStatement',
  'balanceSheet',
  'cashFlow',
];

export const AssetFinancialsWidget = ({ entity }: AssetFinancialsProps) => {
  const { t } = useTranslation('widget');

  const { params, setQueryParams } = useQueryParams();

  const activeFinancialsStatement = (params[
    queryKeys.activeFinancialsStatement
  ] || financialStatementButtonGroupLabels[0]) as FinancialStatementType;
  const activeFinancialsTimeframe = (params[
    queryKeys.activeFinancialsTimeframe
  ] || timeFrames[0]) as TimeFrame;
  const { data, isFetching } = useFinancialStatement({
    entityTag: entity.tag,
    type: activeFinancialsStatement,
    timeframe: activeFinancialsTimeframe,
  });

  const activeStatementButtonGroupIndex =
    financialStatementButtonGroupLabels.indexOf(activeFinancialsStatement);

  const handleStatementButtonGroupSelection = (i: number) => {
    setQueryParams({
      [queryKeys.activeFinancialsStatement]:
        financialStatementButtonGroupLabels[i],
    });
  };

  const activeTimeframeButtonGroupIndex = timeFrames.indexOf(
    activeFinancialsTimeframe
  );

  const handleTimeframeButtonGroupSelection = (i: number) => {
    setQueryParams({ [queryKeys.activeFinancialsTimeframe]: timeFrames[i] });
  };

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              maxWidth: '1280px',
              borderRadius: '8px',
              height: '360px',
              margin: '20px auto',
            },
          },
        ]}
        data-testid="asset-financials-loader"
      />
    );
  }

  if (!data) {
    return (
      <Shared.StyledErrorWrapper
        variant="empty"
        displayIcon
        title={t('widget:financials.empty.title')}
        subTitle={t('widget:financials.empty.subTitle', { asset: entity.name })}
        data-testid="asset-financials-error-message"
      />
    );
  }

  return (
    <FinancialTable
      data={data}
      activeStatementButtonGroupIndex={activeStatementButtonGroupIndex}
      handleStatementButtonGroupSelection={handleStatementButtonGroupSelection}
      activeTimeframeButtonGroupIndex={activeTimeframeButtonGroupIndex}
      handleTimeframeButtonGroupSelection={handleTimeframeButtonGroupSelection}
    />
  );
};
